import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import { Container, Input, Link, Select } from "../components/styled.ts";
import { Typography } from "../components/typography/index.tsx";
import {
  WhatsAppIcon,
  Tick,
  TelegramIcon,
  ClockIcon,
  InvestmentIcon,
  ChartBar,
  Envelope,
  PhoneCallIcon,
  LinkedInIcon,
  InstagramIcon,
  XIcon,
} from "../components/icons/index.tsx";
import Card from "../components/card/index.tsx";
import ImageCard from "../components/imageCard/index.tsx";
import LinkButton from "../components/linkButton/index.tsx";
import { LinkIcon } from "../components/linkIcon/index.tsx";
import { useMobile } from "../hooks/isMobile.tsx";
import { Header } from "../components/header/index.tsx";
import { Seo } from "../components/seo/index.tsx";
import { subscriberContext } from "../context/subscriberContext.tsx";
import { API_URLS } from "../utils/apiConfig.ts";
import { track, trackLand } from "../utils/analytics.ts";
import DropDown from "../components/dropdown/index.tsx";
import AlertShowcase from "../components/alertShowcase/index.tsx";
import TrustBadges from "../components/trustBadges/index.tsx";
import Testimonials from "../components/testimonials/index.tsx";
import FAQ from "../components/faq/index.tsx";

const HomeScreen = () => {
  const { isMobile } = useMobile();
  const [mobileNumber, setMobileNumber] = useState({
    countryCode: "+91",
    phoneNumber: "",
  });
  const [isPhoneNumberEmpty, setIsPhoneNumberEmpty] = useState(false);
  const history = useHistory();
  const signupRef = useRef<HTMLDivElement>(null);

  const { subscriberDetails, fetchSubscriberDetails } =
    useContext(subscriberContext);

  // carousel images for mobile hero
  const carouselImages = [
    "/alerts/bhel_order.png",
    "/alerts/maninds.png",
    "/alerts/sonacoms.png",
    "/alerts/unominda.png"
  ];
  const carouselLabels = ["Order Win", "Market Update", "Results Announcement", "Financial Results"];
  const [carouselIndex, setCarouselIndex] = useState(0);

  // autoplay carousel every 3 seconds
  useEffect(() => {
    const id = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % carouselImages.length);
    }, 3000);
    return () => clearInterval(id);
  }, [carouselImages.length]);


  const sendOtp = async () => {
    history.push({
      pathname: "/otp",
      search: window.location.search,
      state: mobileNumber,
    });
    try {
      const response = await fetch(API_URLS.OTP, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(mobileNumber),
      });
      if (response.ok) {
        track("otp_sent");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    if (subscriberDetails?.phoneNumber) {
      const redirectPath = new URLSearchParams(window.location.search).get("redirectTo");
      if (redirectPath) {
        history.push(`/${redirectPath.replace(/^\//, "")}`);
      } else {
        history.push("/watch-list");
      }
      return;
    }
    window.scrollTo({ top: 0 });
    trackLand();
  }, [subscriberDetails]);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSignup = () => {
    if (signupRef.current) {
      signupRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div>
      <Seo
        title="WhatsApp Stock Alerts for NSE & BSE | FinQ India"
        description="Get real-time NSE & BSE alerts for your stocks on WhatsApp — results, dividends, orders and board meetings, summarised by AI."
        path="/"
      />

      <a href="/" style={{ position: 'absolute', top: isMobile ? 8 : 16, left: isMobile ? 12 : 24, zIndex: 10 }}>
        <img src="/FinQ_Logo_W.png" alt="FinQ logo" style={{ height: isMobile ? 32 : 48, display: 'block' }} />
      </a>
      <a
        href="/subscription-plans"
        style={{
          position: 'absolute',
          top: isMobile ? 12 : 20,
          right: isMobile ? 12 : 24,
          zIndex: 10,
          color: '#fff',
          fontFamily: 'Neue Montreal',
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 500,
          textDecoration: 'none',
        }}
      >
        Subscription Plans
      </a>
      <Container
        justifyContent="center"
        margin="auto"
        column
        padding={isMobile ? "60px 20px 20px 20px" : "60px"}
        width="min(calc(100% - 0px) , 700px)"
        gap={"30px"}
        style={{
          position: "relative",
          width: isMobile ? "100%" : "100vw",
          marginLeft: isMobile ? 0 : "calc(50% - 50vw)",
          backgroundImage: "url('/bg_image.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          display: "flex",
          gap: "16px",
        }}
      >
        {/* Trust badge positioned at the bottom of this container */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 12px",
            background: "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)",
            zIndex: 5,
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            tag="span"
            fontFamily="Neue Montreal"
            fontSize={isMobile ? "12px" : "16px"}
            fontWeight={500}
            color="#09121B"
            style={{ lineHeight: "20px", textAlign: "center", width: "100%" }}
          >
            Join 10,000+ active investors getting real-time stock alerts
          </Typography>
        </div>
        <Container
          padding={"10px"}
          width="100%"
          alignItems="center"
          justifyContent="center"
          gap="8px"
          column={isMobile} // stack vertically on mobile
          margin={isMobile ? "10px 0 0 0" : "20px 0 20px 0"} // increased space below this container
        >
          <Typography
            tag="h1"
            fontFamily="Neue Montreal"
            fontSize={isMobile ? "16px" : "20px"}
            fontWeight={"500"}
            lineHeight={"20px"}
            letterSpacing={"0.02em"}
            color={"#fff"}
            margin={"0"}
            style={{ whiteSpace: isMobile ? "normal" : "nowrap", textAlign: "center" }}
          >
            No Noise! Just Real-Time NSE & BSE Stock Alerts on
          </Typography>
          <img
            src="/whatsapp_text_w.png"
            alt="WhatsApp"
            style={{
              display: "block",
              margin: isMobile ? "0 30px 0 0" : 0,
              width: isMobile ? 160 : 150,
              objectFit: "contain",
            }}
          />
        </Container>
        <Container padding={isMobile ? "0" : "10px"} width="100%" justifyContent="center" column={isMobile}>
          <Container width={"min(100%, 351px)"} column justifyContent="center" alignItems="center" padding={isMobile ? "40px" : "0"}>
            {/* sliding track inside an overflow-hidden frame */}
            <Container
              width="100%"
              style={{
                overflow: 'hidden',
                borderRadius: isMobile ? '12px' : '24px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  width: `${carouselImages.length * 100}%`,
                  transform: `translateX(-${carouselIndex * 100}%)`,
                  transition: 'transform 500ms ease',
                }}
              >
                {carouselImages.map((src, idx) => (
                  <div key={src} style={{ flex: '0 0 100%', maxWidth: '100%' }}>
                    <img
                      src={src}
                      height={"400px"}
                      alt={`Stock Alert ${idx + 1}`}
                      style={{
                        width: "min(400px, 100%)",
                        // height: isMobile ? '220px' : '360px',
                        // maxHeight: isMobile ? '220px' : '420px',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>
                ))}
              </div>
            </Container>

            {/* text labels instead of dots */}
            <Container alignItems="center" justifyContent="center" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
              {carouselLabels.map((label, d) => (
                d === carouselIndex ? (
                  <Container
                    key={d}
                    padding="6px 12px"
                    radius="16px"
                    background="#fff"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCarouselIndex(d)}
                  >
                    <Typography
                      tag="span"
                      fontFamily="Neue Montreal"
                      fontSize="12px"
                      fontWeight={600}
                      color="#09121B"
                    >
                      {label}
                    </Typography>
                  </Container>
                ) : null
              ))}
            </Container>
          </Container>
          <Container
            column
            width={isMobile ? "100%" : ""}
            padding={isMobile ? "0" : "20px"}
            gap="16px"
            style={{ order: isMobile ? -1 : 0 }} // show first on mobile by changing flex order
          >
            <Container alignItems="center" gap={"5px"} padding="10px 0">
              <Typography
                tag="p"
                fontFamily="Neue Montreal"
                fontSize={"24px"}
                fontStyle={"italic"}
                fontWeight={"500"}
                lineHeight={"24px"}
                letterSpacing={"0.02em"}
                // color={"#fff"}
                background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
                WebkitBackgroundClip="text"
                WebkitTextFillColor="transparent"
                margin={"10px auto"}
              >
                Sign Up / Login
              </Typography>
            </Container>
            <Container radius="100px" border="1px solid #ffffff" alignItems="center" background="#09121B">
              <DropDown
                onSelect={(countryCode: any) => {
                  setMobileNumber({ ...mobileNumber, countryCode });
                }}
                countryCode={mobileNumber.countryCode}
              />
              <Container border="solid 0.5px #fff" height="48px" margin="0 10px 0 0"></Container>
              <Input
                type="text"
                placeholder="Enter WhatsApp Number"
                value={mobileNumber.phoneNumber}
                onChange={(e: { target: { value: string } }) => {
                  if (e.target.value.match(/^[0-9]*$/)) {
                    setIsPhoneNumberEmpty(false);
                    setMobileNumber({
                      ...mobileNumber,
                      phoneNumber: e.target.value,
                    });
                  }
                }}
              />
            </Container>
            {isPhoneNumberEmpty && (
              <Typography
                tag="p"
                fontFamily="Neue Montreal"
                fontSize={"16px"}
                fontStyle={"italic"}
                lineHeight={"24px"}
                letterSpacing={"0.02em"}
                color={"red"}
                margin={"0 auto"}
              >
                Please enter number before login
              </Typography>
            )}
            <Container
              padding="13px 20px"
              background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
              radius="100px"
              justifyContent="center"
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (!mobileNumber.phoneNumber) {
                  setIsPhoneNumberEmpty(true);
                  return;
                }
                sendOtp();
              }}
            >
              <Typography
                fontFamily="Neue Montreal"
                fontSize={"16px"}
                lineHeight={"22.4px"}
                fontWeight={500}
                color={"#fff"}
              >
                <Link active>Get OTP</Link>
              </Typography>
            </Container>

            <Container
              border="1px solid #BFB3B3"
              padding="10px"
              radius="24px"
              gap="10px"
              margin="20px 0 0 0"
              background="#09121B"
              column
            >
              <Container gap="5px">
                <Container
                  width="20px"
                  height="20px"
                  alignItems="center"
                  padding="1px 3px"
                  radius="10px"
                  background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
                >
                  <Tick color="#fff" width="15" height="15" />
                </Container>
                <Typography
                  tag="p"
                  fontFamily="Neue Montreal"
                  fontSize={"14px"}
                  fontWeight={500}
                  lineHeight={"20.8px"}
                  letterSpacing={"0.02em"}
                  color="#ffffffff"
                >
                  Trial Period for New Users: 15 days for 20 stocks
                </Typography>
              </Container>
              <Container gap="5px">
                <Container
                  alignItems="center"
                  width="20px"
                  height="20px"
                  padding="1px 3px"
                  radius="24px"
                  background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
                >
                  <Tick color="#fff" width="15" height="15" />
                </Container>
                <Typography
                  tag="p"
                  fontFamily="Neue Montreal"
                  fontSize={"14px"}
                  fontWeight={500}
                  lineHeight={"20.8px"}
                  letterSpacing={"0.02em"}
                  color="#ffffffff"
                >
                  Free Lifetime Plan: Track up to 5 stocks for a lifetime
                </Typography>
              </Container>
            </Container>
          </Container>
        </Container>
      </Container>

      {/* ====== WHY INVESTORS CHOOSE FINQ ====== */}
      <Container
        column
        width="min(calc(100% - 40px) , 1200px)"
        justifyContent="center"
        alignItems="center"
        margin="35px auto"
        gap="20px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "42px"}
          lineHeight={isMobile ? "36px" : "50px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          display="inline"
          fontWeight={500}
          textAlign="center"
        >
          Why Investors Choose{" "}
          <Typography
            tag="span"
            background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            FinQ India
          </Typography>
        </Typography>
        <Container column={isMobile} gap={"36px"}>
          <Card
            background={"#D69DFF"}
            color={"#fff"}
            description={"Results & Order Updates in Real-Time"}
            subDescription={
              "Know the moment companies announce quarterly results, board meetings, or receive major orders. Get alerts for 7,000+ BSE and NSE stocks including SME stocks, REIT, etc."
            }
            icon={<ChartBar color="#D69DFF" height="32" width="32" />}
          />
          <Card
            background={"#5395ff"}
            color={"#fff"}
            description={"Never Miss a Dividend Announcement"}
            icon={<ClockIcon color="#5395ff" width="32" height="32" />}
            subDescription={
              "Get instant WhatsApp alerts when companies declare dividends, bonus issues, or stock splits—before you see it in the news"
            }
          />
          <Card
            background={"#202020"}
            color={"#fff"}
            icon={<InvestmentIcon color="#202020" width="32" height="32" />}
            description={"Save Hours Daily, Never Miss a Trade"}
            subDescription={
              "Stop checking apps constantly. Get all your portfolio alerts in one place—WhatsApp. Make faster, smarter investment decisions"
            }
            subDescColor={"#828282"}
          />
        </Container>
      </Container>

      {/* ====== HOW IT WORKS ====== */}
      <Container
        column
        width="min(calc(100% - 40px) , 1200px)"
        justifyContent="center"
        alignItems="center"
        margin="50px auto"
        gap="25px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "42px"}
          lineHeight={isMobile ? "36px" : "50px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          display="inline"
          fontWeight={500}
        >
          How it{" "}
          <Typography
            tag="span"
            background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
            WebkitBackgroundClip="text"
            WebkitTextFillColor="transparent"
          >
            Works
          </Typography>{" "}
        </Typography>
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={"14px"}
          fontWeight={400}
          lineHeight={"21px"}
          letterSpacing={"0.02em"}
          color="#828282"
          width={isMobile ? "auto" : "50%"}
          textAlign="center"
        >
          Simple Steps to Stay Informed: Sign Up, Create Your Watchlist, and
          Receive Instant Alerts on WhatsApp!
        </Typography>
        <Container width="100%" justifyContent="center" style={{ marginTop: 8 }}>
          {(() => {
            const steps = [
              { src: "howitworks/1.png", label: "Enter Mobile Number" },
              { src: "howitworks/2.png", label: "Enter OTP" },
              { src: "howitworks/3.png", label: "Add Stocks to Watchlist" },
              { src: "howitworks/4.png", label: "Get Alert in WhatsApp" },
            ];
            return (
              <Container
                width="100%"
                style={{ display: "flex", justifyContent: "space-between", gap: isMobile ? 12 : 14, flexWrap: "wrap" }}
              >
                {steps.map((s, i) => (
                  <Container
                    key={i}
                    column
                    alignItems="center"
                    style={{ flex: isMobile ? "0 0 48%" : "0 0 23%", marginBottom: isMobile ? 12 : 0 }}
                  >
                    <img
                      src={s.src}
                      alt={s.label}
                      style={{
                        width: "100%",
                        //maxWidth: isMobile ? 160 : 220,
                        height: "auto",
                        borderRadius: 12,
                        objectFit: "contain",
                        display: "block",
                        background: "#fff",
                        padding: 0,
                      }}
                    />
                    <Typography
                      tag="p"
                      fontFamily="Neue Montreal"
                      fontSize={isMobile ? "12px" : "14px"}
                      fontWeight={400}
                      color="#202020"
                      margin="12px 0 0 0"
                      textAlign="center"
                    >
                      {s.label}
                    </Typography>
                  </Container>
                ))}
              </Container>
            );
          })()}
        </Container>
      </Container>

      {/* ====== TESTIMONIALS ====== */}
      <Container
        column
        width="min(calc(100% - 40px) , 1200px)"
        justifyContent="center"
        alignItems="center"
        margin="60px auto"
        gap="30px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "42px"}
          lineHeight={isMobile ? "36px" : "50px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          fontWeight={600}
          textAlign="center"
        >
          Trusted by Active Investors
        </Typography>
        <Testimonials />
      </Container>

      {/* ====== FAQ ====== */}
      <Container
        column
        width="min(calc(100% - 40px) , 1200px)"
        justifyContent="center"
        alignItems="center"
        margin="60px auto"
        gap="30px"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "42px"}
          lineHeight={isMobile ? "36px" : "50px"}
          letterSpacing={"-0.01em"}
          color="#202020"
          fontWeight={600}
          textAlign="center"
        >
          Frequently Asked Questions
        </Typography>
        <FAQ />
      </Container>

      {/* ====== FOOTER CTA ====== */}
      <Container
        background="#000"
        padding="50px 0 0 0"
        column
        alignItems="center"
      >
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={isMobile ? "28px" : "50px"}
          lineHeight={isMobile ? "43.2px" : "60px"}
          letterSpacing={"-0.01em"}
          color="#fff"
          display="inline"
          fontWeight={500}
          textAlign="center"
          width="min(calc(100% - 40px) , 800px)"
        >
          Experience AI-Powered Stock Market Alerts with FinQ India
        </Typography>
        <Typography
          tag="p"
          fontFamily="Neue Montreal"
          fontSize={"14px"}
          fontWeight={400}
          lineHeight={"21px"}
          letterSpacing={"0.02em"}
          color="#828282"
          width="min(calc(100% - 40px) , 500px)"
          textAlign="center"
        >
          With FinQ India, receive real-time, intelligent alerts for the stock
          market events that matter most. Make smarter, faster investment
          decisions and stay ahead of the market—effortlessly.
        </Typography>
        <Container
          justifyContent="center"
          column={isMobile}
          margin={isMobile ? "24px 0" : "46px 0"}
          gap="12px"
          width="min(calc(100% - 40px) , 720px)"
        >
          <LinkButton
            background="#fff"
            border="solid 1px #fff"
            color="#202020"
            icon={<Envelope color="#202020" width="24" height="24" />}
            linkText="Email for support"
            url="mailto:info@konsritech.com"
          />
          <LinkButton
            background="#000"
            border=" 1px solid rgba(255, 255, 255, 0.64)"
            color="#fff"
            icon={<PhoneCallIcon color="#fff" width="24" height="24" />}
            linkText="+91-8179617744"
            url="tel:+918179617744"
          />
          <LinkButton
            background="#000"
            border="1px solid rgba(255, 255, 255, 0.64)"
            color="#fff"
            icon={<WhatsAppIcon width="24" height="24" />}
            linkText="Chat on WhatsApp"
            url="https://wa.me/918179617744"
          />
        </Container>
        <Container
          alignItems="center"
          gap={"10px"}
          width="min(calc(100% - 40px) , 500px)"
        >
          <Typography
            flex={1}
            borderTop={"solid 1px rgba(255, 255, 255, 0.62)"}
          />
          <Typography
            tag="p"
            fontFamily="Neue Montreal"
            fontSize={"14px"}
            lineHeight={"24px"}
            letterSpacing={"0.02em"}
            color={" rgba(255, 255, 255, 0.62)"}
            margin={"0 auto"}
          >
            Follow Us
          </Typography>
          <Typography
            flex={1}
            borderTop={"solid 1px rgba(255, 255, 255, 0.62)"}
          />
        </Container>
        <Container
          justifyContent="center"
          margin="25px 0"
          gap={isMobile ? "38px" : "58px"}
        >
          <LinkIcon
            url="https://www.instagram.com/finq_india/"
            icon={
              <InstagramIcon
                color="#fff"
                opacity="0.62"
                height="24"
                width="24"
              />
            }
          />
          <LinkIcon
            url="https://x.com/FinQ_India"
            icon={<XIcon color="#fff" opacity="0.62" height="24" width="24" />}
          />
        </Container>
      </Container>

      <Container
        background="#000"
        width="100%"
        justifyContent="space-evenly"
        padding="20px 0"
        style={{ borderTop: "solid 1px rgba(255, 255, 255, 0.62)" }}
        column={isMobile}
      >
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="About Us"
          url="/about-us"
        />
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="FinQ Daily Wrap"
          url="/news"
        />
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="Disclaimer"
          url="/disclaimer"
        />
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="Terms & Conditions"
          url="/terms"
        />
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="Refund Policy"
          url="/refunds-policy"
        />
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="Privacy Policy"
          url="/privacy"
        />
      </Container>
      <Container
        background="#000"
        width="100%"
        justifyContent="space-evenly"
        padding="20px 0"
        style={{ borderTop: "solid 1px rgba(255, 255, 255, 0.62)" }}
      >
        <LinkButton
          border="#000"
          background="#000"
          color="rgba(255,255,255, 0.62)"
          linkText="© 2024-25 KONSRI TECHNOLOGIES PRIVATE LIMITED. All rights reserved."
        />
      </Container>

      {/* Sticky CTA for Mobile */}
      {isMobile && showStickyCTA && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px 20px",
            background: "#fff",
            boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              padding: "14px 24px",
              background: "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)",
              borderRadius: "100px",
              cursor: "pointer",
              width: "100%",
              maxWidth: "400px",
              textAlign: "center",
            }}
            onClick={scrollToSignup}
          >
            <Typography
              fontFamily="Neue Montreal"
              fontSize="15px"
              lineHeight="22px"
              fontWeight={600}
              color="#fff"
              textAlign="center"
            >
              Start Free Trial →
            </Typography>
          </div>
        </div>
      )}

      {/* Bottom spacer for sticky CTA on mobile */}
      {isMobile && <div style={{ height: "70px" }} />}
    </div>
  );
};

export default HomeScreen;
